import { useCallback, useEffect, useRef, useState } from 'react'
import { CameraOff } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { scanFeedback } from '../lib/scan-feedback'
import { cn } from '@/lib/utils'

/**
 * Live-camera barcode/QR scanner for the count capture screen.
 *
 * `barcode-detector` (the standard BarcodeDetector API, native where the browser has it, a WASM
 * zxing ponyfill everywhere else — iPhone Safari has no native implementation) is dynamically
 * imported so its ~1 MB WASM payload never lands in the main backoffice bundle.
 *
 * Detection runs on an interval rather than per frame, single-flighted so a decode slower than
 * the interval cannot overlap the next one (overlapping decodes are what made the preview
 * stutter), and against a cropped, downscaled copy of the guide rectangle rather than the full
 * frame — a code outside the guide is not what the counter is aiming at, and decoding fewer
 * pixels is proportionally faster.
 *
 * A repeat hit on the same code within DEDUPE_MS captures nothing, but is no longer silent: it
 * beeps twice low and shows a short "already captured" pill, so the counter stops re-aiming at a
 * code that already registered. That signal fires once per episode rather than once per tick —
 * a label left in frame keeps decoding every DETECT_INTERVAL_MS, and blipping and buzzing eight
 * times a second is worse than saying nothing. Detection is fully paused (interval cleared, no
 * detect calls) while `paused` is true — a sheet is open, or manual search has the screen.
 */

const DETECT_INTERVAL_MS = 120
const DEDUPE_MS = 2000
/**
 * Share of the (square) preview handed to the detector: the visible guide rectangle plus a small
 * margin, so a code the counter placed just inside the guide is still fully in the crop.
 */
const GUIDE_FRACTION = 0.9
/** Longest edge handed to the detector — beyond this the extra pixels only cost time. */
const MAX_DECODE_EDGE = 640
const HIT_FLASH_MS = 350
const DUPLICATE_PILL_MS = 1200

export interface BarcodeScannerProps {
  paused: boolean
  onHit: (code: string) => void
}

type ScannerState =
  | 'starting'
  | 'running'
  | 'permission-denied'
  | 'unsupported'
  | 'error'

/**
 * Draws the guide region of the current video frame into `canvas`, downscaled to at most
 * MAX_DECODE_EDGE. The preview is `aspect-square object-cover`, so what the counter sees is the
 * centred square of the camera frame and the guide is a centred share of that square.
 */
function drawGuideRegion(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): boolean {
  const { videoWidth, videoHeight } = video
  if (videoWidth === 0 || videoHeight === 0) return false
  const visible = Math.min(videoWidth, videoHeight)
  const source = visible * GUIDE_FRACTION
  const sourceX = (videoWidth - source) / 2
  const sourceY = (videoHeight - source) / 2
  const edge = Math.round(Math.min(source, MAX_DECODE_EDGE))
  if (canvas.width !== edge || canvas.height !== edge) {
    canvas.width = edge
    canvas.height = edge
  }
  const context = canvas.getContext('2d')
  if (!context) return false
  context.drawImage(video, sourceX, sourceY, source, source, 0, 0, edge, edge)
  return true
}

export function BarcodeScanner({ paused, onHit }: BarcodeScannerProps) {
  const { t } = useTranslation('inventory-count')
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const detectingRef = useRef(false)
  const detectorRef = useRef<{
    detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>
  } | null>(null)
  const lastHitRef = useRef<{ code: string; at: number } | null>(null)
  const lastDuplicateSignalAtRef = useRef(0)
  const [state, setState] = useState<ScannerState>('starting')
  const [flash, setFlash] = useState(false)
  const [duplicate, setDuplicate] = useState(false)
  const signalTimers = useRef<Array<ReturnType<typeof setTimeout>>>([])

  const stopInterval = useCallback(() => {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const cancelledRef = useRef(false)

  useEffect(
    () => () => {
      signalTimers.current.forEach(clearTimeout)
      signalTimers.current = []
    },
    [],
  )

  useEffect(() => {
    cancelledRef.current = false

    async function start() {
      try {
        const mod = await import('barcode-detector')
        if (cancelledRef.current) return
        detectorRef.current = new mod.BarcodeDetector({
          formats: ['ean_13', 'qr_code'],
        })

        // Explicit constraints rather than the browser default: the default picks whatever the
        // device considers convenient, which on iPhones is a low-resolution stream that fails on
        // small EAN labels.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        })
        // TS narrows cancelledRef.current as still `false` here (no reassignment it can see
        // between the two awaits), but the effect cleanup genuinely can run concurrently and flip
        // it — that's the whole point of the flag.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (cancelledRef.current) {
          stream.getTracks().forEach((tr) => tr.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play()
        }
        setState('running')
      } catch (err) {
        if (cancelledRef.current) return
        if (
          err instanceof DOMException &&
          (err.name === 'NotAllowedError' || err.name === 'SecurityError')
        ) {
          setState('permission-denied')
        } else if (
          err instanceof DOMException &&
          err.name === 'NotFoundError'
        ) {
          setState('unsupported')
        } else {
          setState('error')
        }
      }
    }

    start()

    return () => {
      cancelledRef.current = true
      stopInterval()
      streamRef.current?.getTracks().forEach((tr) => tr.stop())
      streamRef.current = null
    }
  }, [stopInterval])

  const signal = useCallback((kind: 'hit' | 'duplicate') => {
    if (kind === 'hit') {
      scanFeedback.hit()
      setFlash(true)
      signalTimers.current.push(setTimeout(() => setFlash(false), HIT_FLASH_MS))
    } else {
      scanFeedback.duplicate()
      setDuplicate(true)
      signalTimers.current.push(
        setTimeout(() => setDuplicate(false), DUPLICATE_PILL_MS),
      )
    }
    // Android bonus; Safari implements no vibration API at all, which is why the tones exist.
    if ('vibrate' in navigator) navigator.vibrate(80)
  }, [])

  useEffect(() => {
    if (state !== 'running') return
    if (paused) {
      stopInterval()
      return
    }

    intervalRef.current = setInterval(() => {
      const video = videoRef.current
      const detector = detectorRef.current
      if (!video || !detector || video.readyState < 2) return
      if (detectingRef.current) return
      canvasRef.current ??= document.createElement('canvas')
      if (!drawGuideRegion(video, canvasRef.current)) return
      detectingRef.current = true
      detector
        .detect(canvasRef.current)
        .then((results) => {
          if (results.length === 0) return
          const code = results[0].rawValue
          const now = Date.now()
          const last = lastHitRef.current
          if (last && last.code === code && now - last.at < DEDUPE_MS) {
            // One signal per episode: the same label stays decodable for as long as it is held in
            // frame, so without this the blip and the vibration repeat on every tick.
            if (now - lastDuplicateSignalAtRef.current >= DUPLICATE_PILL_MS) {
              lastDuplicateSignalAtRef.current = now
              signal('duplicate')
            }
            return
          }
          lastHitRef.current = { code, at: now }
          signal('hit')
          onHit(code)
        })
        .catch(() => {
          // A transient decode failure on one frame is not an error state; just try again.
        })
        .finally(() => {
          detectingRef.current = false
        })
    }, DETECT_INTERVAL_MS)

    return stopInterval
  }, [state, paused, onHit, signal, stopInterval])

  if (
    state === 'permission-denied' ||
    state === 'unsupported' ||
    state === 'error'
  ) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/40 p-8 text-center">
        <CameraOff className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">
          {state === 'permission-denied'
            ? t('scanner.permissionDenied')
            : t('scanner.unavailable')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('scanner.useSearchInstead')}
        </p>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden rounded-lg bg-black">
      <video
        ref={videoRef}
        className="aspect-square w-full object-cover"
        muted
        playsInline
      />
      {state === 'starting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-sm text-white">
          {t('scanner.starting')}
        </div>
      )}
      {state === 'running' && (
        <div className="pointer-events-none absolute inset-6 rounded-lg border-2 border-white/70" />
      )}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 bg-emerald-400/60 transition-opacity duration-150',
          flash ? 'opacity-100' : 'opacity-0',
        )}
      />
      {duplicate && (
        <p
          aria-live="polite"
          className="absolute inset-x-4 bottom-4 rounded-full bg-black/80 px-3 py-2 text-center text-sm font-medium text-white"
        >
          {t('scanner.duplicate')}
        </p>
      )}
    </div>
  )
}
